import { useEffect, useState } from 'react';
import { StoreProvider, useStore } from '@/relatos/store';
import BoardIndex from '@/components/forum/index';
import BoardView from '@/components/forum/board';
import ThreadView from '@/components/forum/thread';
import { LoginPanel } from '@/components/forum/auth';
import { shortNpub } from '@/components/forum/bits';
import './forum.css';

function useHashRoute() {
  const [hash, setHash] = useState(window.location.hash);
  useEffect(() => {
    const on = () => { setHash(window.location.hash); window.scrollTo(0, 0); };
    window.addEventListener('hashchange', on);
    return () => window.removeEventListener('hashchange', on);
  }, []);
  const parts = hash.replace(/^#\/?/, '').split('/');
  if (parts[0] === 'tos') return { page: 'tos' as const };
  if (parts[0] === 'b' && parts[1]) return { page: 'board' as const, boardId: parts[1] };
  if (parts[0] === 't' && parts[1] && parts[2]) return { page: 'thread' as const, boardId: parts[1], threadId: parts[2] };
  if (parts[0] === 'join' && parts[1]) return { page: 'join' as const, token: decodeURIComponent(parts.slice(1).join('/')) };
  return { page: 'index' as const };
}

function JoinPage({ token }: { token: string }) {
  const { signer, joinWithInvite } = useStore();
  const [state, setState] = useState<'idle' | 'busy' | 'error'>('idle');
  const [err, setErr] = useState('');

  const redeem = async () => {
    setState('busy');
    try {
      const id = await joinWithInvite(token);
      window.location.hash = `#/b/${id}`;
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
      setState('error');
    }
  };

  return (
    <div>
      <div className="rl-cat">Board invitation</div>
      <div className="rl-panel" style={{ borderTop: 'none', borderRadius: '0 0 3px 3px' }}>
        {!signer ? (
          <p>You’ve been invited to an encrypted board. Sign in (or generate a key) first —
             the invite will be checked against your pubkey.</p>
        ) : (
          <>
            <p>You’ve been invited to an encrypted board. The invite is cryptographically bound
               to your pubkey; redeeming it reveals the board key to you alone.</p>
            <button className="rl-btn rl-btn-primary" onClick={redeem} disabled={state === 'busy'}>
              {state === 'busy' ? 'Verifying & decrypting…' : 'Redeem invite'}
            </button>
            {state === 'error' && <p style={{ color: 'var(--rl-bad)' }}>{err}</p>}
          </>
        )}
        {!signer && <div style={{ marginTop: 14 }}><LoginPanel /></div>}
      </div>
    </div>
  );
}

function TosPage() {
  return (
    <div className="rl-panel">
      <div className="rl-h2">Terms of Service</div>
      <p><strong>1. Adults only.</strong> Relatos is an uncensored, user-run forum and boards may
        contain adult content. By using this service you affirm that you are at least 18 years old
        (or the age of majority in your jurisdiction). This affirmation is recorded on your device
        at entry.</p>
      <p><strong>2. No proactive monitoring.</strong> We do not monitor content proactively. Users are
        solely responsible for what they post. Illegal content reported to us will be removed where
        technically possible and reported to the relevant authorities, including
        [PLACEHOLDER: hkfhf.gov]. Board admins moderate their own boards; the platform
        moderator can be reached via the Report button on any post.</p>
      <p className="rl-small" style={{ opacity: 0.75 }}>
        Technical note: private-board content is end-to-end encrypted and cannot be read by relay
        operators or this deployment. “Removal” of encrypted content means deletion requests
        (NIP-09) and relay-side pruning; copies held by other clients cannot be revoked — this is
        a property of the protocol, stated honestly.
      </p>
    </div>
  );
}

function Chrome({ children, crumbs }: { children: React.ReactNode; crumbs: React.ReactNode }) {
  const { signer, logout, boards, relayHealth } = useStore();
  const boardCount = Object.keys(boards).length;
  const relayTotal = Object.keys(relayHealth).length;
  const relayUp = Object.values(relayHealth).filter(Boolean).length;
  return (
    <>
      <header className="rl-header">
        <div className="rl-header-inner">
          <div>
            <a className="rl-logo" href="#/">Relatos</a>
            <div className="rl-tagline">private boards on nostr — relays see ciphertext, members see community</div>
          </div>
          <nav className="rl-nav">
            <a href="#/">Board index</a>
            {signer
              ? <>
                  <span className="rl-pill" style={{ background: 'rgba(255,255,255,0.12)', border: 'none', color: '#dbe9f7' }}>
                    {shortNpub(signer.pubkey)}
                  </span>
                  <button onClick={logout}>Log out</button>
                </>
              : <span className="rl-pill" style={{ background: 'rgba(255,255,255,0.12)', border: 'none', color: '#dbe9f7' }}>lurking</span>}
          </nav>
        </div>
      </header>
      <main className="rl-wrap">
        <div className="rl-crumbs">{crumbs}</div>
        {children}
        <footer className="rl-footer">
          <span>
            <strong>{boardCount}</strong> board{boardCount === 1 ? '' : 's'} held on this device ·
            end-to-end encrypted · epoch keys never leave your browser
            {relayTotal > 0 && <> · relays: <strong>{relayUp}/{relayTotal}</strong> up</>}
          </span>
          <span><a href="#/tos">Terms</a> · 18+ · Relatos — phpBB soul, nostr transport, NIP-44 vault</span>
        </footer>
      </main>
    </>
  );
}

function Shell() {
  const route = useHashRoute();
  const { signer, boards } = useStore();

  let crumbs: React.ReactNode = <a href="#/">Board index</a>;
  let body: React.ReactNode;

  if (route.page === 'tos') {
    crumbs = <><a href="#/">Board index</a> » Terms of Service</>;
    body = <TosPage />;
  } else if (!signer && route.page !== 'join') {
    body = <LoginPanel />;
  } else if (route.page === 'join') {
    crumbs = <><a href="#/">Board index</a> » Invitation</>;
    body = <JoinPage token={route.token} />;
  } else if (route.page === 'board') {
    const name = boards[route.boardId]?.meta?.name;
    crumbs = <><a href="#/">Board index</a> » {name ?? 'Encrypted board'}</>;
    body = <BoardView boardId={route.boardId} />;
  } else if (route.page === 'thread') {
    const name = boards[route.boardId]?.meta?.name;
    crumbs = <><a href="#/">Board index</a> » <a href={`#/b/${route.boardId}`}>{name ?? 'Encrypted board'}</a> » Topic</>;
    body = <ThreadView boardId={route.boardId} threadId={route.threadId} />;
  } else {
    body = <BoardIndex />;
  }

  return <Chrome crumbs={crumbs}>{body}</Chrome>;
}

export default function App() {
  return (
    <StoreProvider>
      <Shell />
    </StoreProvider>
  );
}
